import {
  EHeaderBidder_PoddedRequestType,
  IBidderWithTableData,
  IPublisher,
} from '~/src/dash/api/console-api';
import { DemandSource, DemandSources } from '~/src/dash/globals/demand-sources';
import {
  EHeaderBidder_PodSequenceTargeting,
  EHeaderBidder_PositionTargeting,
  EHeaderBidder_TargetedAdBreak,
} from '~/src/dash/api/headerbidders/headerbidders_models';
import {
  parseNumber,
  parseObject,
  parseString,
  transformModel,
} from '~/src/dash/api/resource-transforms';
import { IMenuList } from '~/src/dash/types/menuList';
import { isPhiloPublisher, isSamsungNAPublisher, isTestPublisher } from '~/src/dash/utils/ui';

export function HeaderBidderPositionTargetingToString(m: EHeaderBidder_PositionTargeting): string {
  switch (m) {
    case EHeaderBidder_PositionTargeting.HeaderBidder_PositionTargeting_ALL:
      return 'All';
    case EHeaderBidder_PositionTargeting.HeaderBidder_PositionTargeting_PREROLL:
      return 'Preroll';
    case EHeaderBidder_PositionTargeting.HeaderBidder_PositionTargeting_MIDROLL:
      return 'Midroll';
    case EHeaderBidder_PositionTargeting.HeaderBidder_PositionTargeting_POSTROLL:
      return 'Postroll';
    default:
      return 'Unknown';
  }
}

export function HeaderBidderDealAtTargetingToString(m: EHeaderBidder_DealAtTargeting): string {
  switch (m) {
    case EHeaderBidder_DealAtTargeting.HeaderBidder_DealAtTargeting_None:
      return 'None';
    case EHeaderBidder_DealAtTargeting.HeaderBidder_DealAtTargeting_First_Price:
      return 'First Price (value = 1)';
    case EHeaderBidder_DealAtTargeting.HeaderBidder_DealAtTargeting_Second_Price_Plus:
      return 'Second Price Plus (value = 2)';
    case EHeaderBidder_DealAtTargeting.HeaderBidder_DealAtTargeting_Fixed_Price:
      return 'Fixed Price (value = 3';
    default:
      return 'Unknown';
  }
}

export function HeaderBidderPodSequenceTargetingToString(
  m: EHeaderBidder_PodSequenceTargeting,
): string {
  switch (m) {
    case EHeaderBidder_PodSequenceTargeting.HeaderBidder_PodSequenceTargeting_ANYPOD:
      return 'Any Pod';
    case EHeaderBidder_PodSequenceTargeting.HeaderBidder_PodSequenceTargeting_FIRSTPOD:
      return 'First Pod';
    case EHeaderBidder_PodSequenceTargeting.HeaderBidder_PodSequenceTargeting_LASTPOD:
      return 'Last Pod';
    case EHeaderBidder_PodSequenceTargeting.HeaderBidder_PodSequenceTargeting_FIRSTANDLASTPOD:
      return 'First and Last Pod';
    default:
      return 'Unknown';
  }
}

export function HeaderBidderTargetedAdBreakToString(m: EHeaderBidder_TargetedAdBreak): string {
  switch (m) {
    case EHeaderBidder_TargetedAdBreak.HeaderBidder_TARGETED_ADBREAK_ALL:
      return 'All';
    case EHeaderBidder_TargetedAdBreak.HeaderBidder_TARGETED_ADBREAK_FIRST:
      return 'First';
    case EHeaderBidder_TargetedAdBreak.HeaderBidder_TARGETED_ADBREAK_LAST:
      return 'Last';
    default:
      return 'Unknown';
  }
}

export function HeaderBidderPoddedRequestTypeToString(m: EHeaderBidder_PoddedRequestType): string {
  switch (m) {
    case EHeaderBidder_PoddedRequestType.HeaderBidderPoddedRequestTypeStructured:
      return 'Structured';
    case EHeaderBidder_PoddedRequestType.HeaderBidderPoddedRequestTypeDynamic:
      return 'Dynamic';
    default:
      return 'Unknown';
  }
}

export function convertPriorityToBidderTier(priority: string): string {
  switch (priority) {
    case '11':
      return '0';
    case '10':
      return '1';
    case '9':
      return '2';
    case '8':
      return '3';
    case '7':
      return '4';
    case '6':
      return '5';
    case '5':
      return '6';
    case '4':
      return '7';
    case '3':
      return '8';
    case '2':
      return '9';
    case '1':
      return '10';
    default:
      return '0';
  }
}

export interface IPriorityOption {
  text: string;
  value: number;
}

// generates all the sub-priority options for the sub-priority dropdown
export function generateSubPriorityOptions(): IPriorityOption[] {
  const options: IPriorityOption[] = [];

  for (let subPriority = 10; subPriority > 0; subPriority--) {
    options.push({
      text: `Subtier ${convertPriorityToBidderTier(subPriority.toString())}`,
      value: subPriority,
    });
  }

  return options;
}

// Bidder tiers 7 - 10 do not support custom names (HB-3071)
export const generatePriorityOptions = (
  publisher: IPublisher,
  showAll: boolean,
  demandSource: string,
): IPriorityOption[] => {
  const priorityOptions = [
    {
      text: publisher.BidderTier1CustomName
        ? `Tier 1 - ${publisher.BidderTier1CustomName}`
        : 'Tier 1',
      value: 10,
    },
    {
      text: publisher.BidderTier2CustomName
        ? `Tier 2 - ${publisher.BidderTier2CustomName}`
        : 'Tier 2',
      value: 9,
    },
    {
      text: publisher.BidderTier3CustomName
        ? `Tier 3 - ${publisher.BidderTier3CustomName}`
        : 'Tier 3',
      value: 8,
    },
    {
      text: publisher.BidderTier4CustomName
        ? `Tier 4 - ${publisher.BidderTier4CustomName}`
        : 'Tier 4',
      value: 7,
    },
    {
      text: publisher.BidderTier5CustomName
        ? `Tier 5 - ${publisher.BidderTier5CustomName}`
        : 'Tier 5',
      value: 6,
    },
    {
      text: publisher.BidderTier6CustomName
        ? `Tier 6 - ${publisher.BidderTier6CustomName}`
        : 'Tier 6',
      value: 5,
    },
    {
      text: publisher.BidderTier7CustomName
        ? `Tier 7 - ${publisher.BidderTier7CustomName}`
        : 'Tier 7',
      value: 4,
    },
    {
      text: publisher.BidderTier8CustomName
        ? `Tier 8 - ${publisher.BidderTier8CustomName}`
        : 'Tier 8',
      value: 3,
    },
    {
      text: publisher.BidderTier9CustomName
        ? `Tier 9 - ${publisher.BidderTier9CustomName}`
        : 'Tier 9',
      value: 2,
    },
    {
      text: publisher.BidderTier10CustomName
        ? `Tier 10 - ${publisher.BidderTier10CustomName}`
        : 'Tier 10',
      value: 1,
    },
  ] as IPriorityOption[];

  const tier0Option: IPriorityOption = { text: 'Tier 0', value: 11 };

  if (
    (isSamsungNAPublisher(publisher.ID) ||
      isPhiloPublisher(publisher.ID) ||
      isTestPublisher(publisher.ID)) &&
    [
      DemandSources.platformConnection,
      DemandSources.dynamicPlatformConnection,
      DemandSources.gateway,
      DemandSources.dynamicGateway,
    ].includes(demandSource as DemandSource)
  ) {
    priorityOptions.unshift(tier0Option);
  }

  if (
    (isSamsungNAPublisher(publisher.ID) ||
      isPhiloPublisher(publisher.ID) ||
      isTestPublisher(publisher.ID)) &&
    DemandSources.gatewayTier0 === demandSource
  ) {
    return [tier0Option];
  }

  if (showAll) {
    priorityOptions.unshift({ text: 'All', value: 0 });
  }

  return priorityOptions;
};

export const generatePriorityOptionsMultiList = (
  publisher: IPublisher,
  showAll: boolean,
  demandSource: string,
): IMenuList => {
  const menuList: IMenuList = {};

  generatePriorityOptions(publisher, showAll, demandSource).forEach(
    (iPriorityOption: IPriorityOption) => {
      const tier = convertPriorityToBidderTier(iPriorityOption.value.toString());
      menuList[tier] = {
        ID: tier,
        selected: true,
        title: `${iPriorityOption.text}`,
        value: iPriorityOption.value,
      };
    },
  );

  return menuList;
};

export const transformBidderWithTableData = transformModel<IBidderWithTableData>({
  ID: parseNumber(['ID']),
  Name: parseString(['Name']),
  tableData: parseObject(['tableData'], (obj: any) => {
    return obj;
  }),
});

export const transformBidderWithTableDataWrapper = (fromObj: any) => {
  return transformBidderWithTableData(fromObj);
};
